import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePresence, PresenceUser } from '../../hooks/usePresence.js';
import toast from 'react-hot-toast';

interface PresenceCanvasProps {
  userId: string;
  userName: string;
  userColor?: string;
  wsUrl?: string;
  className?: string;
  children?: React.ReactNode;
  sessionId?: string;
}

interface CursorProps {
  user: PresenceUser;
  isVisible: boolean;
  canvasRef: React.RefObject<HTMLDivElement>;
}

const Cursor: React.FC<CursorProps & { 'data-testid'?: string; canvasRef: React.RefObject<HTMLDivElement> }> = ({ user, isVisible, canvasRef, 'data-testid': testId }) => {
  if (!user.cursor || !canvasRef.current) {
    return null;
  }

  const domX = user.cursor.x;
  const domY = user.cursor.y;
  
  console.log(`[Client] Rendering cursor for ${user.name}: received coords (${user.cursor.x},${user.cursor.y}), positioning at DOM: (${domX.toFixed(1)},${domY.toFixed(1)})`);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className="absolute pointer-events-none z-50"
          data-testid={testId}
          style={{
            left: `${domX}px`,
            top: `${domY}px`,
            transform: 'translate(-2px, -2px)',
            position: 'absolute',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="drop-shadow-lg"
          >
            <path
              d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
              fill={user.color}
              stroke="white"
              strokeWidth="1"
            />
          </svg>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-6 left-2 bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap"
            style={{ backgroundColor: user.color }}
          >
            {user.name}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PresenceCanvas: React.FC<PresenceCanvasProps> = ({
  userId,
  userName,
  userColor = '#3B82F6',
  wsUrl = import.meta.env.VITE_WS_URL ? `${import.meta.env.VITE_WS_URL}/presence` : 'wss://hivecore-backend-production.up.railway.app/presence',
  className = '',
  children,
  sessionId,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isMouseInCanvas, setIsMouseInCanvas] = useState(false);

  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [showReconnectBanner, setShowReconnectBanner] = useState(false);

  const handleUserJoin = useCallback((user: PresenceUser) => {
    console.log('PresenceCanvas: User joined:', user);
    toast.success(`${user.name} has joined this view`, {
      duration: 3000,
      icon: '👋',
      style: {
        background: '#1F2937',
        color: '#F9FAFB',
        border: `1px solid ${user.color}`,
      },
    });
    
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `${user.name} has joined the collaborative workspace`;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }, []);

  const handleUserLeave = useCallback((userId: string) => {
    console.log('PresenceCanvas: User left:', userId);
  }, []);

  const {
    users,
    isConnected,
    connectionState,
    retryCount,
    lastError,
    updateCursor,
    updateSelection,
    status,
  } = usePresence({
    url: wsUrl,
    userId,
    userName,
    userColor,
    sessionId: sessionId || userId,
    onUserJoin: handleUserJoin,
    onUserLeave: handleUserLeave,
  });

  useEffect(() => {
    if (users.size > 0) {
      const userArray = Array.from(users.values());
      userArray.forEach(user => {
        if (user.id !== userId) {
          toast(`${user.name} has left this view`, {
            duration: 2000,
            icon: '👋',
            style: {
              background: '#1F2937',
              color: '#F9FAFB',
            },
          });
          
          const announcement = document.createElement('div');
          announcement.setAttribute('aria-live', 'polite');
          announcement.setAttribute('aria-atomic', 'true');
          announcement.className = 'sr-only';
          announcement.textContent = `${user.name} has left the collaborative workspace`;
          document.body.appendChild(announcement);
          setTimeout(() => document.body.removeChild(announcement), 1000);
        }
      });
    }
  }, [users, userId]);

  useEffect(() => {
    if (retryCount > 3 && !isConnected) {
      setShowReconnectBanner(true);
      setConnectionError('Connection has dropped — retrying…');
    } else if (isConnected) {
      setShowReconnectBanner(false);
      setConnectionError(null);
    }
  }, [retryCount, isConnected]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      console.log(`[Client-${userId}] Mouse move: clientX=${event.clientX}, clientY=${event.clientY}, rect.left=${rect.left}, rect.top=${rect.top}, calculated x=${x}, y=${y}, canvas size=${rect.width}x${rect.height}`);
      
      if (isConnected && x >= 0 && y >= 0 && x <= rect.width && y <= rect.height) {
        console.log(`[Client-${userId}] Sending cursor update to server: x=${x}, y=${y}`);
        updateCursor(x, y);
      }
    };

    const handleMouseEnter = () => {
      setIsMouseInCanvas(true);
    };

    const handleMouseLeave = () => {
      setIsMouseInCanvas(false);
    };

    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && isConnected) {
        const range = selection.getRangeAt(0);
        updateSelection(range.startOffset, range.endOffset);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('selectionchange', handleSelection);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('selectionchange', handleSelection);
    };
  }, [isConnected, updateCursor, updateSelection, userId]);

  const visibleUsers = Array.from(users.values()).filter(
    user => user.id !== userId && Date.now() - user.lastSeen < 30000
  );

  console.log('PresenceCanvas render:', {
    isConnected,
    connectionState,
    usersCount: users.size,
    visibleUsersCount: visibleUsers.length,
    retryCount,
    lastError,
    status
  });


  return (
    <div
      ref={canvasRef}
      className={`relative w-full h-full border-2 border-solid border-red-500 ${className}`}
      style={{ cursor: isMouseInCanvas ? 'none' : 'default', position: 'relative' }}
      data-testid="presence-canvas"
      role="application"
      aria-label="Collaborative workspace with real-time presence"
      aria-describedby="presence-status"
    >
      {children}
      
      {visibleUsers.map((user, index) => (
        <Cursor
          key={`${user.id}-${index}`}
          user={user}
          isVisible={true}
          canvasRef={canvasRef}
          data-testid="remote-cursor"
        />
      ))}

      {showReconnectBanner && (
        <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-black px-4 py-2 text-sm z-50">
          <div className="flex items-center justify-between">
            <span>{connectionError}</span>
            <button
              onClick={() => window.location.reload()}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs"
            >
              Retry Now
            </button>
          </div>
        </div>
      )}


      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {visibleUsers.length > 0 && `${visibleUsers.length} other users are currently active in this workspace`}
      </div>
    </div>
  );
};

export default PresenceCanvas;
